import { useMemo } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import useComboBoxState, { ComboBoxOption } from './useComboBoxState'
import { splitOptionKey, toMuiComboBoxProps, toTextFieldProps } from './muiProps'
import { ComboBoxOptionOption, ComboBoxProps } from './types'

export default function ComboBoxMui<T>( {
	setSelectedOption,
	selectedOption,
	data,
	getName,
	setSearchString,
	debounceDelayMs,
	async = true,
	renderOption,
	onSearchChange,
	...props
}: ComboBoxProps<T> ) {
	const { inputVal, setInputVal, buildOption, options } = useComboBoxState( { data, getName, setSearchString, debounceDelayMs } )
	const { autocomplete, textField, suppressOptions } = toMuiComboBoxProps( props )

	// buildOption allocates a fresh object every call; useAutocomplete resets the
	// visible input text whenever the `value` prop's reference changes. Memoizing
	// on `selectedOption` alone (not `options`, which is rebuilt on every search)
	// keeps identity stable through typing and data refetches, and — unlike
	// matching into `options` by display label — can't resolve to the wrong row
	// when two options share a label.
	const value = useMemo( () => ( selectedOption ? buildOption( selectedOption ) : null ), [ selectedOption ] )

	// Pickers can hydrate a selection before its search returns, so the selected
	// option isn't always in `data`. MUI warns on a value outside `options`.
	const withSelected = value && !options.some( ( option ) => option.value === value.value )
		? [ value, ...options ]
		: options

	return (
		<Autocomplete<ComboBoxOption<T>>
			{...autocomplete}
			options={suppressOptions ? [] : withSelected}
			value={value}
			onChange={( _event, next ) => setSelectedOption( next?.value )}
			onInputChange={( _event, next, reason ) => {
				// Selecting, blurring and `value` changes write the option label back into
				// the input; EUI's onSearchChange only reacts to user edits.
				if ( reason !== 'input' && reason !== 'clear' ) {
					return
				}

				setInputVal( next )
				onSearchChange?.( next )
			}}
			getOptionLabel={( option ) => option.label}
			isOptionEqualToValue={( option, selected ) => option.value === selected.value}
			// EUI treats `async` options as pre-filtered by the caller's query.
			filterOptions={async ? ( unfiltered ) => unfiltered : undefined}
			renderOption={renderOption
				? ( optionProps, option ) => {
					const { key, liProps } = splitOptionKey( optionProps )

					return (
						<Box component="li" key={key ?? option.label} {...liProps}>
							{renderOption( option as ComboBoxOptionOption<T>, inputVal, '' )}
						</Box>
					)
				}
				: undefined}
			renderInput={( params ) => (
				<TextField {...toTextFieldProps( params, textField )} />
			)}
		/>
	)
}
